import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";

import {
  PRACTICE_NAME_IRRIGATED,
  PRACTICE_NAME_NON_IRRIGATED,
} from "@datanac/datanac-api-toolkit/dist/datanac-rma-api-helper";
import { calculate_cecl_from_stresstest } from "@datanac/datanac-api-toolkit/dist/insurance/stress-test/cecl";
import { calculateStressTest } from "@datanac/datanac-api-toolkit/dist/insurance/stress-test/crop/crop_stress_test";
import { DatesChart } from "@datanac/datanac-visualization-toolkit/dist/Components/rma-adm/DatesChart";
import { YieldVarianceChart } from "@datanac/datanac-visualization-toolkit/dist/Components/rma-adm/YieldVarianceChart";
import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import ArrowDropDownOutlined from "@mui/icons-material/ArrowDropDownOutlined";
import CalculateOutlined from "@mui/icons-material/CalculateOutlined";
import CalendarMonthOutlined from "@mui/icons-material/CalendarMonthOutlined";
import CheckOutlined from "@mui/icons-material/CheckOutlined";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import { default as EastOutlined } from "@mui/icons-material/EastOutlined";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Icon,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { ApiHelper, RmaApiHelper, UsersApiHelper } from "api/ApiHelper";
import { AppContext } from "App";
import { BUDGET_STATUS_ACTIVE } from "components/Budget/helpers";
import EmptyCard from "components/Cards/EmptyCard";
import { ArrowDownIcon, BudgetIcon, InsuranceIcon } from "components/Icons/Icons";
import InsuranceOptimizeDataGrid from "components/InsuranceOptimizeDataGrid";
import { MODULE_ARC_PLC } from "components/Menu/config/constants";
import { getUniqueCounties } from "components/Vault/Farm/helpers";
import WorkspaceActionBar from "components/Workspaces/WorkspaceActionBar";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import PopupState, { bindMenu, bindTrigger } from "material-ui-popup-state";
import { SkeletonWrapper } from "mui-toolkit/SkeletonWrapper";
import { useSnackbar } from "notistack";
import { formatNumber, formatPercentage, formatPrice } from "utility/formatters";
import { v4 as uuidv4 } from "uuid";

import { isMcoPlan } from "../../api-toolkit/mco/isMcoPlan";
import { loadMcoProjectedPriceFromAdm } from "../../api-toolkit/mco/mcoAreaInputs";
import {
  calculateCombinedArcCoResults,
  getShareWeightedHistoricalIrrigationPercentage,
  useArcPlc,
} from "../../api-toolkit/useArcPlc";
import { useChatAssistantAttachments } from "../../contexts/useChatAssistantAttachments";
import { PROGRAM_NAME_ARCCO, PROGRAM_NAME_PLC } from "../ArcPlc/ArcPlcService";
import { ArcCoStressTestGrid } from "../ArcPlc/components/ArcCoStressTestGrid";
import { PlcStressTestGrid } from "../ArcPlc/components/PlcStressTestGrid";
import { AddInsuranceDialog } from "./components/AddInsuranceDialog";
import InsuranceCoverageOverlapChart from "./components/InsuranceCoverageOverlapChart";
import InsurancePlanCard from "./components/InsurancePlanCard";
import InsuranceStressTestParametersDialog from "./components/InsuranceStressTestParametersDialog";
import McoSection from "./components/McoSection";
import { OptimizeInsuranceDialog } from "./components/OptimizeInsuranceDialog";
import { PremiumDetailsDialog } from "./components/PremiumDetailsDialog";
import {
  calculateStressTestDisplayFieldLabel,
  STRESS_TEST_FIELD_INDEMNITY_TOTAL_PER_ACRE,
  STRESS_TEST_FIELD_NET_INSURANCE_BENEFIT_PER_ACRE,
  STRESS_TEST_FIELD_NET_PROFIT_INSURED,
  STRESS_TEST_FIELD_NET_PROFIT_SIMPLE,
} from "./helpers";
import { InsuranceService } from "./InsuranceService";

// Display mode constants (same as ArcPlcCountyBaseAcreWorkspace)
const DISPLAY_MODE_PLANTED_ACRE = "planted_acre";
const DISPLAY_MODE_BASE_ACRE = "base_acre";

export default function InsuranceScenarioWorkspace() {
  const { globalState } = useContext(AppContext);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("lg"), { noSsr: true });
  const { insurance_scenario_id, state_name, county_name, commodity_name, type_name, practice_name } = useParams();
  const navigate = useNavigate();
  const { addAttachments } = useChatAssistantAttachments();

  const isXXL = useMediaQuery("(min-width:1537px)");
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("md"), { noSsr: true });
  const { enqueueSnackbar } = useSnackbar();

  const [isInsuranceDatesDialogOpen, setIsInsuranceDatesDialogOpen] = useState(false);

  // --- --- ---

  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { preventDuplicate: true, variant: "error" });
  };

  const [insurancePlans, setInsurancePlans] = useState([]);

  // MCO runs in its own simulation universe — never feed it into the toolkit's stress
  // test, optimizer, or coverage-overlap chart (those don't know its plan names/math).
  const mcoPlans = useMemo(() => insurancePlans?.filter(isMcoPlan) ?? [], [insurancePlans]);
  const nonMcoPlans = useMemo(() => insurancePlans?.filter((p) => !isMcoPlan(p)) ?? [], [insurancePlans]);
  const mcoOnlyScenario = useMemo(
    () => Boolean(mcoPlans.length) && !nonMcoPlans.length,
    [mcoPlans.length, nonMcoPlans.length]
  );

  // --- --- ---

  const [insuranceScenario, setInsuranceScenario] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadInsuranceScenario = useCallback(async () => {
    setIsLoading(true);
    try {
      const producerFilter = {
        producer_token: globalState.producer_token,
        id: insurance_scenario_id,
        is_active: true,
      };

      const _insuranceScenario = await UsersApiHelper.users_selectObject(
        "InsuranceScenario",
        producerFilter,
        globalState
      );

      setInsuranceScenario(_insuranceScenario?.length ? _insuranceScenario[0] : null);
    } catch (err) {
      setInsuranceScenario(null);
      handleError(err);
    } finally {
      setIsLoading(false);
    }
  }, [globalState.producer_token, insurance_scenario_id]);

  useEffect(() => {
    if (globalState?.producer_token) {
      loadInsuranceScenario();
    }
  }, [globalState?.producer_token]);

  // --- --- ---

  const [budgetCountyPractice, setBudgetCountyPractice] = useState(null);
  const [isLoadingBudgetCountyPractice, setIsLoadingBudgetCountyPractice] = useState(true);

  const loadBudgetCountyPractice = useCallback(async () => {
    if (!insuranceScenario?.crop_year) return;

    setIsLoadingBudgetCountyPractice(true);
    try {
      const _budgetFilter = {
        state_name,
        county_name,
        commodity_name,
        type_name,
        practice_name,
        crop_year: insuranceScenario.crop_year,
        year: insuranceScenario.crop_year,
        status: BUDGET_STATUS_ACTIVE,
        is_active: true,
      };

      const _budgets = await UsersApiHelper.selectBudgetCountyPractices(_budgetFilter, globalState);

      const _cropFilter = {
        crop_year: insuranceScenario.crop_year,
        location_state_name: state_name,
        location_county_name: county_name,
        commodity_name,
        type_name,
        practice_name,
        is_active: true,
      };

      const _crops = await UsersApiHelper.selectProducerFarmFieldCrops(_cropFilter, globalState);
      const _cropsCountyPractice = getUniqueCounties(_crops);

      if (_budgets?.length) {
        const _activeBudgetCountyPractice = { ..._budgets[0] };

        _activeBudgetCountyPractice.reinsurance_year = _activeBudgetCountyPractice?.year;
        _activeBudgetCountyPractice.production_cost_per_acre =
          _activeBudgetCountyPractice?.reported_acreage > 0
            ? _activeBudgetCountyPractice?.production_cost / _activeBudgetCountyPractice?.reported_acreage
            : 0;

        if (_cropsCountyPractice?.length) {
          const _currentCropCountyPractice = _cropsCountyPractice[0];

          Object.assign(_activeBudgetCountyPractice, {
            approved_yield: _currentCropCountyPractice.approved_yield_weighted,
            rate_yield: _currentCropCountyPractice.rate_yield_weighted,
            adjusted_yield: _currentCropCountyPractice.adjusted_yield_weighted,
            share_percent: _currentCropCountyPractice.share_percent_weighted,
          });
        }

        setBudgetCountyPractice(_activeBudgetCountyPractice);
      } else {
        setBudgetCountyPractice(null);
      }
    } catch (err) {
      setBudgetCountyPractice(null);
      handleError(err);
    } finally {
      setIsLoadingBudgetCountyPractice(false);
    }
  }, [
    insuranceScenario?.crop_year,
    state_name,
    county_name,
    commodity_name,
    type_name,
    practice_name,
    globalState?.producer_token,
  ]);

  useEffect(() => {
    if (insuranceScenario) {
      loadBudgetCountyPractice();
    }
  }, [insuranceScenario]);

  // --- --- ---

  const [programElection, setProgramElection] = useState(null);
  const loadProgramElection = useCallback(async () => {
    if (!insuranceScenario?.crop_year) return;

    try {
      const programElectionFilter = {
        crop_year: insuranceScenario.crop_year,
        state_name: state_name,
        county_name: county_name,
        commodity_name: commodity_name,
        program_category: MODULE_ARC_PLC,
        is_active: true,
      };
      const _programElections = await UsersApiHelper.users_selectObject(
        "ProgramElection",
        programElectionFilter,
        globalState
      );
      if (_programElections?.length) {
        setProgramElection(_programElections[0]);
      } else {
        setProgramElection(null);
      }
    } catch (err) {
      setProgramElection(null);
      handleError(err);
    }
  }, [insuranceScenario?.crop_year, state_name, county_name, commodity_name, globalState?.producer_token]);

  useEffect(() => {
    if (insuranceScenario && globalState?.producer_token) {
      loadProgramElection();
    } else {
      setProgramElection(null);
    }
  }, [insuranceScenario, globalState?.producer_token, loadProgramElection]);

  // --- --- ---

  const handleBudgetNavigateClick = () => {
    if (budgetCountyPractice) {
      navigate(`/budget/${budgetCountyPractice?.budget_id}/${budgetCountyPractice?.id}`);
    }
  };

  // --- --- ---

  const [countyData, setCountyData] = useState(null);

  const loadCountyData = useCallback(async () => {
    if (!budgetCountyPractice) return;

    try {
      let _currentCounty = {
        reinsurance_year: budgetCountyPractice.year,
        state_name,
        county_name,
        commodity_name,
        type_name,
        practice_name,
      };

      let currentPrices = await RmaApiHelper.selectPrices(_currentCounty);

      if (!currentPrices?.length) {
        currentPrices = await RmaApiHelper.selectPrices({
          ..._currentCounty,
          reinsurance_year: budgetCountyPractice.year - 1,
        });

        if (currentPrices.length) {
          currentPrices[0] = {
            ...currentPrices[0],
            projected_price: budgetCountyPractice?.projected_price,
            is_prior_year: true,
          };
        }
      }

      const currentCountyData = await RmaApiHelper.selectCountyData(_currentCounty);

      const currentCounty = {
        ...(_currentCounty || {}),
        ...(currentPrices.length ? currentPrices[0] : {}),
        reinsurance_year: budgetCountyPractice.year,
        expected_index_value: currentCountyData?.length ? currentCountyData[0].expected_index_value : null,
      };

      setCountyData(currentCounty);
    } catch (err) {
      console.error("Error loading county data:", err);
      setCountyData(null);
    }
  }, [
    budgetCountyPractice?.year,
    budgetCountyPractice?.projected_price,
    state_name,
    county_name,
    commodity_name,
    type_name,
    practice_name,
  ]);

  useEffect(() => {
    if (budgetCountyPractice) {
      loadCountyData();
    }
  }, [budgetCountyPractice]);

  // MCO publishes its own ADM projected price (different discovery window from RP). Show it
  // whenever ADM has MCO offers/prices — not only after an MCO plan is saved on the scenario.
  const [mcoProjectedPrice, setMcoProjectedPrice] = useState(null);
  useEffect(() => {
    if (!budgetCountyPractice) {
      setMcoProjectedPrice(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const price = await loadMcoProjectedPriceFromAdm(
          {
            stateName: state_name,
            countyName: county_name,
            commodityName: commodity_name,
            type_name,
            practice_name,
          },
          budgetCountyPractice.year,
          { insurancePlanName: mcoPlans[0]?.insurance_plan_name }
        );
        if (!cancelled) setMcoProjectedPrice(price ?? mcoPlans[0]?.projected_price ?? null);
      } catch (err) {
        console.warn("MCO projected price load failed", err);
        if (!cancelled) setMcoProjectedPrice(mcoPlans[0]?.projected_price ?? null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    mcoPlans,
    budgetCountyPractice?.year,
    state_name,
    county_name,
    commodity_name,
    type_name,
    practice_name,
  ]);

  // --- --- ---

  const [isLoadingInsurancePlans, setIsLoadingInsurancePlans] = useState(true);
  const [calculatePremiumsResponse, setCalculatePremiumsResponse] = useState(null);
  const loadInsurancePlans = useCallback(async () => {
    if (!insuranceScenario || !budgetCountyPractice) return;

    try {
      setIsLoadingInsurancePlans(true);

      const _InsuranceService = new InsuranceService({
        insuranceScenarios: [insuranceScenario].filter((is) => is),
        budgetCountyPractices: [budgetCountyPractice].filter((cp) => cp),
        crop_year: insuranceScenario.crop_year,
        apiConfig: globalState,
      });
      const _insuranceScenarioPopulated = await _InsuranceService.actions.loadInsuranceScenario();

      if (
        _insuranceScenarioPopulated?.length &&
        _insuranceScenarioPopulated[0].budgetCountyPractices?.length &&
        _insuranceScenarioPopulated[0].budgetCountyPractices[0]?.insurancePlans?.length
      ) {
        setInsurancePlans(_insuranceScenarioPopulated[0].budgetCountyPractices[0]?.insurancePlans);

        _InsuranceService.actions
          .calculatePremiums(_insuranceScenarioPopulated[0])
          .then((calculatePremiumsResponseInsuranceScenario) => {
            setCalculatePremiumsResponse(calculatePremiumsResponseInsuranceScenario);
            setInsurancePlans(calculatePremiumsResponseInsuranceScenario?.budgetCountyPractices[0]?.insurancePlans);

            setIsLoadingInsurancePlans(false);
          });
      } else {
        setInsurancePlans([]);
        setCalculatePremiumsResponse(null);
        setIsLoadingInsurancePlans(false);
      }
    } catch (err) {
      setIsLoadingInsurancePlans(false);
      setInsurancePlans(null);
      setCalculatePremiumsResponse(null);
      handleError(err);
    }
  }, [globalState?.producer_token, insuranceScenario?.id, insuranceScenario?.crop_year, budgetCountyPractice?.id]);

  useEffect(() => {
    if (globalState?.producer_token && insuranceScenario && budgetCountyPractice) {
      loadInsurancePlans();
    } else {
      setInsurancePlans([]);
      setCalculatePremiumsResponse(null);
    }
  }, [globalState?.producer_token, insuranceScenario, budgetCountyPractice]);

  // --- --- ---

  const [stressTestDisplayField, setStressTestDisplayField] = useState(STRESS_TEST_FIELD_NET_PROFIT_INSURED);
  const [isStressTestOptionsDialogOpen, setIsStressTestOptionsDialogOpen] = useState(false);

  const [stressTestParameters, setStressTestParameters] = useState(null);
  const [stressTestResults, setStressTestResults] = useState(null);

  // --- --- ---
  // ARC-CO Comparison

  const [baseAcres, setBaseAcres] = useState(null);

  // Budget County Practices state (needed for stress tests)
  const [budgetCountyPractices, setBudgetCountyPractices] = useState([]);

  // Display mode state (shared by both ARC-CO and PLC)
  const [arcCoDisplayMode] = useState(DISPLAY_MODE_PLANTED_ACRE);

  const budgetTotalReportedAcreage = useMemo(() => {
    return budgetCountyPractices?.length > 0
      ? budgetCountyPractices.reduce((accumulator, currentValue) => accumulator + currentValue?.reported_acreage, 0)
      : 0;
  }, [budgetCountyPractices]);

  const historicalIrrigationPercentage = useMemo(() => {
    if (!baseAcres?.length) return null;
    return getShareWeightedHistoricalIrrigationPercentage(baseAcres);
  }, [baseAcres]);

  // Memoize the entire YieldVarianceChart component to prevent unnecessary re-renders
  const memoizedYieldVarianceChart = useMemo(() => {
    if (!budgetCountyPractice || !insuranceScenario?.crop_year) {
      return <Skeleton />;
    }

    return (
      <YieldVarianceChart
        countyPractice={{
          reinsurance_year: budgetCountyPractice?.reinsurance_year,
          commodity_name: budgetCountyPractice?.commodity_name,
          type_name: budgetCountyPractice?.type_name,
          practice_name: budgetCountyPractice?.practice_name,
          state_name: budgetCountyPractice?.state_name,
          county_name: budgetCountyPractice?.county_name,
        }}
        reinsurance_year_minimum={2014}
        reinsurance_year_maximum={null}
        api={ApiHelper}
        size="small"
        renderer="svg"
      />
    );
  }, [budgetCountyPractice, insuranceScenario?.crop_year]);

  // Initialize useArcPlc with all stress test dependencies
  const arcPlc = useArcPlc({
    baseAcres,
    budgetCountyPractices,
    budgetTotalReportedAcreage,
    historicalIrrigationPercentage,
    arcCoDisplayMode,
    location_state_name: state_name,
    location_county_name: county_name,
    commodity_name,
    crop_year: insuranceScenario?.crop_year,
    // Use the same stress test parameters as the main insurance stress tests
    arcCoStressTestParametersIrrigated: stressTestParameters,
    arcCoStressTestParametersNonIrrigated: stressTestParameters,
    plcStressTestParameters: stressTestParameters,
  });

  // Extract stress test results from hook
  const {
    arcCo: arcCoStressTestResults,
    arcCoIrrigated: arcCoStressTestResultsIrrigated,
    arcCoNonIrrigated: arcCoStressTestResultsNonIrrigated,
    plc: plcStressTestResults,
  } = arcPlc.stressTestResults || {};

  const arcCoCombinedResults = useMemo(() => {
    return calculateCombinedArcCoResults(arcPlc);
  }, [arcPlc]);

  // Additional calculated values needed for stress test grids
  const arcPlcTotalBaseAcreage = useMemo(() => {
    return baseAcres?.length > 0
      ? baseAcres.reduce((accumulator, currentValue) => accumulator + currentValue.base_acreage, 0)
      : 0;
  }, [baseAcres]);

  // Calculate non-irrigated scale factor (needed for ARC-CO grid tooltips)
  const NonIrrigatedScaleFactor = useMemo(() => {
    if (arcPlc?.responses?.length >= 2) {
      const irrigatedResponse = arcPlc.responses.find((r) => r.baseAcres?.practice_name === PRACTICE_NAME_IRRIGATED);
      const nonIrrigatedResponse = arcPlc.responses.find(
        (r) => r.baseAcres?.practice_name === PRACTICE_NAME_NON_IRRIGATED
      );

      if (
        irrigatedResponse?.arcPlcResponse?.arcco?.benchmark_yield &&
        nonIrrigatedResponse?.arcPlcResponse?.arcco?.benchmark_yield
      ) {
        return (
          nonIrrigatedResponse.arcPlcResponse.arcco.benchmark_yield /
          irrigatedResponse.arcPlcResponse.arcco.benchmark_yield
        );
      }
    }
    return null;
  }, [arcPlc?.responses]);

  const loadBaseAcres = useCallback(async () => {
    if (!insuranceScenario) return;

    try {
      const baseAcresFilter = {
        producer_token: globalState.producer_token,
        location_state_name: state_name,
        location_county_name: county_name,
        commodity_name,
        // type_name, practice_name,
        crop_year: insuranceScenario?.crop_year,
        is_active: true,
      };
      const _baseAcres = await UsersApiHelper.users_search("ProducerFarmBaseAcres", baseAcresFilter, globalState);
      if (_baseAcres?.length) {
        setBaseAcres(_baseAcres);
      } else {
        setBaseAcres(null);
      }
    } catch (err) {
      setBaseAcres(null);
      handleError(err);
    }
  }, [
    globalState?.producer_token,
    insuranceScenario?.id,
    insuranceScenario?.crop_year,
    state_name,
    county_name,
    commodity_name,
  ]);

  useEffect(() => {
    if (globalState?.producer_token && insuranceScenario) {
      loadBaseAcres();
    }
  }, [globalState?.producer_token, insuranceScenario]);

  // Budget County Practices loading (needed for stress tests)
  const loadBudgetCountyPractices = useCallback(async () => {
    const _budgetFilter = {
      state_name: state_name,
      county_name: county_name,
      commodity_name: commodity_name,
      year: insuranceScenario?.crop_year,
      status: BUDGET_STATUS_ACTIVE,
      is_active: true,
    };

    try {
      const _budgetCountyPractices = await UsersApiHelper.selectBudgetCountyPractices(_budgetFilter, globalState);
      setBudgetCountyPractices(_budgetCountyPractices);
    } catch (err) {
      setBudgetCountyPractices([]);
      handleError(err);
    }
  }, [state_name, county_name, commodity_name, insuranceScenario?.crop_year, globalState?.producer_token]);

  useEffect(() => {
    if (state_name && county_name && commodity_name && insuranceScenario?.crop_year && globalState?.producer_token) {
      loadBudgetCountyPractices();
    }
  }, [state_name, county_name, commodity_name, insuranceScenario?.crop_year, globalState?.producer_token]);

  // ---

  const loadStressTestParameters = useCallback(async () => {
    if (!globalState?.producer_token || !budgetCountyPractice) return;

    try {
      const filter = {
        producer_token: globalState.producer_token,
        insurance_scenario_id,
        state_name,
        county_name,
        commodity_name,
        type_name,
        practice_name,
        is_active: true,
      };
      const _stressTestParameters = await UsersApiHelper.users_selectObject(
        "InsuranceStressTestParameters",
        filter,
        globalState
      );
      if (_stressTestParameters?.length) {
        setStressTestParameters(_stressTestParameters[0]);
        return _stressTestParameters[0];
      }
    } catch (err) {
      handleError(err);
    }
  }, [
    globalState?.producer_token,
    budgetCountyPractice,
    insurance_scenario_id,
    state_name,
    county_name,
    commodity_name,
    type_name,
    practice_name,
  ]);

  // Load stress test parameters early to ensure useArcPlc gets them
  useEffect(() => {
    if (globalState?.producer_token && budgetCountyPractice) {
      loadStressTestParameters();
    }
  }, [globalState?.producer_token, budgetCountyPractice]);

  useEffect(() => {
    const loadStressTestResults = async () => {
      try {
        const _stressTestParameters = stressTestParameters ? stressTestParameters : await loadStressTestParameters();

        const props = {
          farmParameters: budgetCountyPractice,
          countyParameters: countyData,
          insurancePlanParameters: nonMcoPlans,
          testParameters: _stressTestParameters,
          apiHelper: ApiHelper,
        };

        const _currentStressTestResults = await calculateStressTest(props);

        if (_currentStressTestResults) {
          _currentStressTestResults.cecl = calculate_cecl_from_stresstest({
            stressTestResults: _currentStressTestResults.results,
          });

          setStressTestResults(_currentStressTestResults);
        }
      } catch (err) {
        setStressTestResults([]);
        handleError(err);
      }
    };
    if (globalState?.producer_token && budgetCountyPractice && countyData && nonMcoPlans?.length) {
      if (!isLoadingInsurancePlans) {
        loadStressTestResults();
      } else {
        setStressTestResults([]);
      }
    }
  }, [
    globalState?.producer_token,
    budgetCountyPractice,
    countyData,
    nonMcoPlans,
    isLoadingInsurancePlans,
    stressTestParameters,
    loadStressTestParameters,
  ]);

  // Attach minimal stress test data to chat assistant
  useEffect(() => {
    if (stressTestResults?.results?.length) {
      const minimalStressTestData = {
        scenario: `${commodity_name} - ${type_name} - ${practice_name} (${county_name}, ${state_name})`,
        data: stressTestResults.results.map((column) =>
          column.map((cell) => ({
            price: cell.price,
            yield: cell.yield,
            insured_net_profit: cell.net_profit_insured,
            net_insurance_benefit: cell.net_insurance_benefit_per_acre,
          }))
        ),
      };

      addAttachments([{ name: "Insurance Stress Test Results", content: minimalStressTestData }]);
    }
  }, [stressTestResults, commodity_name, type_name, practice_name, county_name, state_name, addAttachments]);

  const handleStressTestMenuClick = async (_stressTestDisplayField, popupState) => {
    setStressTestDisplayField(_stressTestDisplayField);
    popupState?.close();
  };

  const handleStressTestOptionsDialogOpenClick = async () => {
    setIsStressTestOptionsDialogOpen(true);
  };

  const handleStressTestParametersChange = async (_stressTestParameters) => {
    if (!_stressTestParameters?.id) {
      _stressTestParameters.insurance_scenario_id = insurance_scenario_id;
      _stressTestParameters.id = uuidv4();
      _stressTestParameters.is_active = true;
    }

    setStressTestParameters(_stressTestParameters);

    UsersApiHelper.updateInsuranceStressTestParameters(_stressTestParameters, globalState);
  };

  const handleInsurancePlanCardClick = (insurancePlan) => {
    setSelectedInsurancePlanName(insurancePlan.insurance_plan_name);
    setIsPremiumDetailsDialogOpen(true);
  };

  // --- --- ---

  const [isOptimizeDialogOpen, setIsOptimizeDialogOpen] = useState(false);

  const handleInsurancePlanCardChange = async (_insurancePlan) => {
    if (_insurancePlan) {
      _insurancePlan.insurance_scenario_id = insurance_scenario_id;
      await UsersApiHelper.updateInsurancePlans({ InsurancePlans: [_insurancePlan] }, globalState);
      loadInsurancePlans();
    }
  };

  const handleOptimizerOnChange = async (_insurancePlans) => {
    handleAddPlanSelected(_insurancePlans);
  };

  const handleAddPlanSelected = async (_newInsurancePlans) => {
    if (insurancePlans?.length) {
      insurancePlans?.forEach((p) => {
        p.is_active = false;
      });
    }

    // InsurancePlan.line_number is NOT NULL with no default, and synthesized rows (MCO) skip the
    // toolkit state helper that normally assigns it.
    let _nextLineNumber = Math.max(
      0,
      ...[...insurancePlans, ...(_newInsurancePlans || [])].map((p) => p.line_number || 0)
    );

    _newInsurancePlans?.forEach((p) => {
      p.id = p.id || uuidv4();
      p.line_number = p.line_number || ++_nextLineNumber;
      p.reinsurance_year = insuranceScenario?.crop_year;
      p.insurance_scenario_id = insurance_scenario_id;

      p.state_name = budgetCountyPractice?.state_name;
      p.county_name = budgetCountyPractice?.county_name;
      p.commodity_name = budgetCountyPractice?.commodity_name;
      p.type_name = budgetCountyPractice?.type_name;
      p.practice_name = budgetCountyPractice?.practice_name;

      p.is_active = true;
    });

    try {
      await UsersApiHelper.updateInsurancePlans(
        {
          InsurancePlans: [...insurancePlans, ..._newInsurancePlans],
        },
        globalState
      );
    } catch (err) {
      handleError(err);
    }

    loadInsurancePlans();
  };

  const [isAddInsuranceDialogOpen, setIsAddInsuranceDialogOpen] = useState(false);
  const [isPremiumDetailsDialogOpen, setIsPremiumDetailsDialogOpen] = useState(false);
  const [selectedInsurancePlanName, setSelectedInsurancePlanName] = useState(null);

  // --- --- ---

  const calculateBreakeven = (_loanAmount) => {
    return _loanAmount / budgetCountyPractice?.reported_acreage / budgetCountyPractice?.yield;
  };

  const formMaxWidth = "1000px";
  const headerTitle = `${commodity_name} - ${type_name === "No Type Specified" ? "" : type_name + " - "} ${practice_name}`;
  return (
    <>
      <WorkspaceHeader
        title={headerTitle}
        subtitle={county_name + ", " + state_name}
        category={{
          title: "Insurance",
          icon: <InsuranceIcon />,
        }}
        breadCrumbs={[
          {
            title: "Scenarios",
            path: "/insurance",
          },
          {
            title: insuranceScenario?.name,
            path: `/insurance/${insurance_scenario_id}`,
          },
        ]}
      />

      <Stack direction="row" spacing={2} alignItems="center" className="headerWithMenuButton">
        <Typography variant="h1">Crop Information</Typography>
        {isLoading && <CircularProgress />}
      </Stack>

      <Stack direction="row" spacing={1} className="insurance-scenario-farm">
        <Stack direction="column">
          <SkeletonWrapper isLoading={isLoadingBudgetCountyPractice}>
            <Typography variant="h1">{formatNumber(budgetCountyPractice?.approved_yield, 0)}</Typography>
          </SkeletonWrapper>
          <Typography variant="body1">
            APPROVED YIELD <br />
            (APH) AVG
          </Typography>
        </Stack>

        <Divider orientation="vertical" flexItem />

        <Stack direction="column">
          <SkeletonWrapper isLoading={isLoadingBudgetCountyPractice}>
            <Typography variant="h1">{formatNumber(budgetCountyPractice?.rate_yield, 0)}</Typography>
          </SkeletonWrapper>
          <Typography variant="body1">
            RATE YIELD
            <br /> AVG
          </Typography>
        </Stack>

        <Divider orientation="vertical" flexItem />

        <Stack direction="column">
          <SkeletonWrapper isLoading={isLoadingBudgetCountyPractice}>
            <Typography variant="h1">
              {budgetCountyPractice?.adjusted_yield ? formatNumber(budgetCountyPractice?.adjusted_yield, 0) : "--"}
            </Typography>
          </SkeletonWrapper>
          <Typography variant="body1">
            ADJUSTED YIELD <br /> AVG
          </Typography>
        </Stack>
      </Stack>

      <Divider />

      <Stack direction="row" spacing={1} alignItems="center" className="headerWithMenuButton insurance-scenario">
        <BudgetIcon sx={{ marginRight: "5px !important" }} />
        <Typography variant="h1">Farm Budget</Typography>

        <IconButton onClick={handleBudgetNavigateClick}>
          <EastOutlined />
        </IconButton>
      </Stack>
      <Stack direction="row" spacing={1} className="insurance-scenario-farm">
        <Stack direction="column">
          <SkeletonWrapper isLoading={isLoadingBudgetCountyPractice}>
            <Typography variant="h1" sx={{ whiteSpace: "pre" }}>
              {formatNumber(budgetCountyPractice?.reported_acreage, 0)}
            </Typography>
          </SkeletonWrapper>
          <Typography variant="body1">ACRES</Typography>
        </Stack>

        <Divider orientation="vertical" flexItem />

        <Stack direction="column">
          <SkeletonWrapper isLoading={isLoadingBudgetCountyPractice}>
            <Typography variant="h1">
              {formatPrice(budgetCountyPractice?.production_cost / budgetCountyPractice?.reported_acreage, 0)}
            </Typography>
          </SkeletonWrapper>
          <Typography variant="body1">COST /AC</Typography>
        </Stack>

        <Divider orientation="vertical" flexItem />

        <Stack direction="column">
          <SkeletonWrapper isLoading={isLoadingBudgetCountyPractice}>
            <Typography variant="h1">{formatNumber(budgetCountyPractice?.yield, 0)}</Typography>
          </SkeletonWrapper>
          <Typography variant="body1">
            PROJECTED YIELD
            <br /> AVG
          </Typography>
        </Stack>

        <Divider orientation="vertical" flexItem />

        <Stack direction="column">
          <SkeletonWrapper isLoading={isLoadingBudgetCountyPractice}>
            <Typography variant="h1">{formatPrice(budgetCountyPractice?.projected_price, 2)}</Typography>
          </SkeletonWrapper>
          <Typography variant="body1">BUDGET PRICE</Typography>
        </Stack>

        <Divider orientation="vertical" flexItem />

        <Stack direction="column">
          <SkeletonWrapper isLoading={isLoadingBudgetCountyPractice}>
            <Typography variant="h1" sx={{ whiteSpace: "pre" }}>
              {formatPercentage(budgetCountyPractice?.share_percent, 0)}
            </Typography>
          </SkeletonWrapper>
          <Typography variant="body1">SHARE %</Typography>
        </Stack>
      </Stack>

      <Divider />

      <Stack direction="row" spacing={1} alignItems="center" className="headerWithMenuButton insurance-scenario">
        <Typography variant="h1">Insurance Info</Typography>
      </Stack>
      <Stack direction="row" spacing={1} className="insurance-scenario-farm">
        <Stack direction="column">
          <Typography variant="h1">
            {countyData?.projected_price > 0 ? formatPrice(countyData.projected_price, 4) : "--"}
          </Typography>
          <Typography variant="body1">USDA PROJECTED PRICE</Typography>
        </Stack>

        {mcoProjectedPrice > 0 && (
          <>
            <Divider orientation="vertical" flexItem />
            <Stack direction="column">
              <Typography variant="h1">{formatPrice(mcoProjectedPrice, 4)}</Typography>
              <Typography variant="body1">MCO PROJECTED PRICE</Typography>
            </Stack>
          </>
        )}

        <Divider orientation="vertical" flexItem />

        <Stack direction="column">
          <Typography variant="h1">
            {countyData?.price_volatility_factor != null ? formatNumber(countyData.price_volatility_factor, 2) : "--"}
          </Typography>
          <Typography variant="body1">PRICE VOLATILITY FACTOR</Typography>
        </Stack>

        <Divider orientation="vertical" flexItem />

        <Stack direction="column">
          <Typography variant="h1">
            {countyData?.expected_index_value != null ? formatNumber(countyData.expected_index_value, 1) : "--"}
          </Typography>
          <Typography variant="body1">EXPECTED COUNTY YIELD</Typography>
        </Stack>
      </Stack>

      {Boolean(countyData?.is_prior_year) && (
        <Box sx={{ mt: 2, mb: 2, maxWidth: formMaxWidth }}>
          <Alert severity="warning">Prior year data used for insurance prices.</Alert>
        </Box>
      )}

      <Divider />

      <Stack direction="row" spacing={2} alignItems="center" className="headerWithMenuButton">
        <Typography variant="h1">Insurance Plans</Typography>
        <IconButton
          variant="outlined"
          onClick={() => setIsInsuranceDatesDialogOpen(true)}
          data-testid="calendar-button"
        >
          <CalendarMonthOutlined />
        </IconButton>
        {/* <WorkspaceViewSelector workspaceMode={workspaceMode} setWorkspaceMode={setWorkspaceMode} /> */}
        {isLoadingInsurancePlans && <CircularProgress />}
      </Stack>

      <Box className="cardContainer">
        {Boolean(insurancePlans?.length) &&
          insurancePlans?.map((currentPlan, index) => (
            <InsurancePlanCard
              key={index}
              insurancePlan={currentPlan}
              onChange={handleInsurancePlanCardChange}
              onClick={() => handleInsurancePlanCardClick(currentPlan)}
            />
          ))}
      </Box>

      {!insurancePlans?.length && (
        <Box className="cardContainer">
          <EmptyCard
            text="No insurance plans listed"
            secondaryText="Get started by adding a plan"
            isLoading={isLoadingInsurancePlans}
          />
        </Box>
      )}

      {mcoOnlyScenario && !isLoadingInsurancePlans && (
        <>
          <Divider />
          <Stack direction="row" spacing={2} alignItems="center" className="headerWithMenuButton pageBreakBefore">
            <Typography variant="h1">Margin Coverage Option</Typography>
          </Stack>
          <McoSection
            mcoPlans={mcoPlans}
            basePlan={null}
            farmParameters={budgetCountyPractice}
            countyData={countyData}
            stressTestParameters={stressTestParameters}
            geo={{
              reinsurance_year: budgetCountyPractice?.reinsurance_year ?? budgetCountyPractice?.year,
              state_name,
              county_name,
              commodity_name,
              type_name,
              practice_name,
            }}
          />
        </>
      )}

      {Boolean(stressTestResults?.results?.length) && !mcoOnlyScenario && (
        <>
          <Divider />

          <Stack direction="row" spacing={2} alignItems="center" className="headerWithMenuButton pageBreakBefore">
            <Typography variant="h1">Stress Test</Typography>
            <PopupState variant="popover" popupId="stress-test-popup-menu">
              {(popupState) => (
                <>
                  <Button variant="outlined" {...bindTrigger(popupState)}>
                    {calculateStressTestDisplayFieldLabel(stressTestDisplayField)} <ArrowDropDownOutlined />
                  </Button>
                  <Menu {...bindMenu(popupState)}>
                    <MenuItem
                      onClick={() => handleStressTestMenuClick(STRESS_TEST_FIELD_NET_PROFIT_SIMPLE, popupState)}
                    >
                      {stressTestDisplayField === STRESS_TEST_FIELD_NET_PROFIT_SIMPLE ? (
                        <CheckOutlined sx={{ mr: 1 }} />
                      ) : (
                        <Icon sx={{ mr: 1 }} />
                      )}
                      Net Profit Before Insurance
                    </MenuItem>
                    <MenuItem
                      onClick={() => handleStressTestMenuClick(STRESS_TEST_FIELD_NET_PROFIT_INSURED, popupState)}
                    >
                      {stressTestDisplayField === STRESS_TEST_FIELD_NET_PROFIT_INSURED ? (
                        <CheckOutlined sx={{ mr: 1 }} />
                      ) : (
                        <Icon sx={{ mr: 1 }} />
                      )}
                      Insured Net Profit
                    </MenuItem>

                    <Divider />

                    <MenuItem
                      onClick={() => handleStressTestMenuClick(STRESS_TEST_FIELD_INDEMNITY_TOTAL_PER_ACRE, popupState)}
                    >
                      {stressTestDisplayField === STRESS_TEST_FIELD_INDEMNITY_TOTAL_PER_ACRE ? (
                        <CheckOutlined sx={{ mr: 1 }} />
                      ) : (
                        <Icon sx={{ mr: 1 }} />
                      )}
                      Insurance Benefit
                    </MenuItem>

                    <MenuItem
                      onClick={() =>
                        handleStressTestMenuClick(STRESS_TEST_FIELD_NET_INSURANCE_BENEFIT_PER_ACRE, popupState)
                      }
                    >
                      {stressTestDisplayField === STRESS_TEST_FIELD_NET_INSURANCE_BENEFIT_PER_ACRE ? (
                        <CheckOutlined sx={{ mr: 1 }} />
                      ) : (
                        <Icon sx={{ mr: 1 }} />
                      )}
                      Net Insurance Benefit
                    </MenuItem>
                  </Menu>
                </>
              )}
            </PopupState>

            <Button variant="outlined" onClick={handleStressTestOptionsDialogOpenClick}>
              <CalculateOutlined sx={{ mr: 1 }} /> Options
            </Button>
            <Dialog open={isStressTestOptionsDialogOpen}>
              <InsuranceStressTestParametersDialog
                onClose={() => setIsStressTestOptionsDialogOpen(false)}
                onChange={handleStressTestParametersChange}
                initialStressTestParameters={stressTestParameters}
                budgetCountyPractice={
                  insuranceScenario?.budgetCountyPractices?.length && insuranceScenario?.budgetCountyPractices[0]
                }
                countyData={
                  insuranceScenario?.budgetCountyPractices?.length &&
                  insuranceScenario?.budgetCountyPractices[0]?.countyData
                }
              />
            </Dialog>
          </Stack>

          <Box sx={{ maxWidth: "1050px" }}>
            <InsuranceOptimizeDataGrid
              commodity_name={commodity_name}
              displayFieldName={stressTestDisplayField}
              yAxisFieldName={"price"}
              yAxisDisplayName={"Price"}
              stressTestResults={stressTestResults?.results}
              doMarkNegativeValues={true}
              maxLossValue={-1 * budgetCountyPractice?.production_cost_per_acre}
            />
          </Box>
          <InsuranceCoverageOverlapChart
            budgetCountyPractice={budgetCountyPractice}
            countyData={countyData}
            insurancePlans={nonMcoPlans}
            stressTestDisplayField={stressTestDisplayField}
            arcCoCombinedResults={arcCoCombinedResults}
            arcPlc={arcPlc}
            budgetTotalReportedAcreage={budgetTotalReportedAcreage}
            programElection={programElection}
          />

          <Box sx={{ mt: 2 }}>
            {[STRESS_TEST_FIELD_NET_INSURANCE_BENEFIT_PER_ACRE, STRESS_TEST_FIELD_INDEMNITY_TOTAL_PER_ACRE].includes(
              stressTestDisplayField
            ) &&
              stressTestResults?.insurancePlans?.map((insurancePlan, index) => (
                <Accordion
                  key={`insurance-plan-${index}`}
                  disableGutters
                  sx={{
                    backgroundColor: "background.paper",
                    "&.Mui-expanded": {
                      backgroundColor: "grey.100",
                    },
                    "&:before": {
                      display: "none",
                    },
                    border: "none",
                    borderRadius: "0.875rem !important",
                    boxShadow: "none",
                    mb: 2,
                  }}
                >
                  <AccordionSummary
                    sx={{
                      padding: 3,
                      flexDirection: "row-reverse",
                      "& .MuiAccordionSummary-content": {
                        margin: "0 0 0 1rem",
                      },
                    }}
                    expandIcon={
                      <ArrowDownIcon
                        sx={{
                          width: 48,
                          height: 48,
                          color: "primary.main",
                          opacity: 0.8,
                          "&:hover": {
                            opacity: 1,
                            color: "primary.dark",
                          },
                        }}
                      />
                    }
                  >
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, width: "100%" }}>
                      <Typography
                        sx={{ fontSize: "1.25rem !important", fontWeight: "600 !important", mb: "0 !important" }}
                        variant="h5"
                      >
                        {(() => {
                          let displayName = insurancePlan?.insurance_plan_name;
                          if (
                            Array.isArray(insurancePlan?.insurance_option_codes) &&
                            insurancePlan?.insurance_option_codes.includes("SE")
                          ) {
                            displayName += " (SE)";
                          }
                          return displayName;
                        })()}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={({ "&>.MuiStack-root": { gap: 1 } }, { maxWidth: "1050px" })}>
                    <InsuranceOptimizeDataGrid
                      commodity_name={commodity_name}
                      displayFieldName={stressTestDisplayField}
                      yAxisFieldName={"price"}
                      yAxisDisplayName={"Price"}
                      stressTestResults={stressTestResults?.results}
                      doMarkNegativeValues={true}
                      maxLossValue={-1 * budgetCountyPractice?.production_cost_per_acre}
                      insurance_plan_uuid={insurancePlan?.uuid}
                    />
                  </AccordionDetails>
                </Accordion>
              ))}

            {/* MCO rides in the same accordion structure as the RP/SCO grids (Rogers
                2026-08-07) — same price/yield axes, its own input-cost $/ac control, and
                the MCO Detail button. Everything else MCO lives in that dialog. */}
            {[STRESS_TEST_FIELD_NET_INSURANCE_BENEFIT_PER_ACRE, STRESS_TEST_FIELD_INDEMNITY_TOTAL_PER_ACRE].includes(
              stressTestDisplayField
            ) &&
              Boolean(mcoPlans.length) && (
                <McoSection
                  mcoPlans={mcoPlans}
                  basePlan={nonMcoPlans?.[0]}
                  farmParameters={budgetCountyPractice}
                  countyData={countyData}
                  stressTestParameters={stressTestParameters}
                  geo={{
                    reinsurance_year: budgetCountyPractice?.reinsurance_year,
                    state_name,
                    county_name,
                    commodity_name,
                    type_name,
                    practice_name,
                  }}
                />
              )}
          </Box>

          {[STRESS_TEST_FIELD_NET_INSURANCE_BENEFIT_PER_ACRE, STRESS_TEST_FIELD_INDEMNITY_TOTAL_PER_ACRE].includes(
            stressTestDisplayField
          ) &&
            Boolean(arcCoStressTestResults?.results?.length) &&
            programElection?.program_election === PROGRAM_NAME_ARCCO && (
              <Box sx={{ mt: 2 }}>
                <Accordion
                  disableGutters
                  sx={{
                    backgroundColor: "background.paper",
                    "&.Mui-expanded": {
                      backgroundColor: "grey.100",
                    },
                    "&:before": {
                      display: "none",
                    },
                    border: "none",
                    borderRadius: "0.875rem !important",
                    boxShadow: "none",
                    mb: 2,
                  }}
                >
                  <AccordionSummary
                    sx={{
                      padding: 3,
                      flexDirection: "row-reverse",
                      "& .MuiAccordionSummary-content": {
                        margin: "0 0 0 1rem",
                      },
                    }}
                    expandIcon={
                      <ArrowDownIcon
                        sx={{
                          width: 48,
                          height: 48,
                          color: "primary.main",
                          opacity: 0.8,
                          "&:hover": {
                            opacity: 1,
                            color: "primary.dark",
                          },
                        }}
                      />
                    }
                  >
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, width: "100%" }}>
                      <Typography
                        sx={{ fontSize: "1.25rem !important", fontWeight: "600 !important", mb: "0 !important" }}
                        variant="h5"
                      >
                        ARC-County
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={({ "&>.MuiStack-root": { gap: 1 } }, { maxWidth: "1050px" })}>
                    <ArcCoStressTestGrid
                      commodity_name={commodity_name}
                      displayFieldName={stressTestDisplayField}
                      xAxisFieldName={"county_yield"}
                      xAxisDisplayName={
                        "County Yield" + (arcPlc?.totals?.arccoResponsesByPractice?.length > 1 ? " (Irrigated)" : "")
                      }
                      yAxisFieldName={"price"}
                      yAxisDisplayName={"MYA"}
                      stressTestResults={arcCoStressTestResults?.results}
                      stressTestResultsIrrigated={arcCoStressTestResultsIrrigated?.results}
                      stressTestResultsNonIrrigated={arcCoStressTestResultsNonIrrigated?.results}
                      nonIrrigatedScaleFactor={NonIrrigatedScaleFactor}
                      historicalIrrigationPercentage={historicalIrrigationPercentage}
                      doMarkNegativeValues={true}
                      maxLossValue={-1}
                      displayMode={arcCoDisplayMode}
                      budgetTotalReportedAcreage={budgetTotalReportedAcreage || arcPlcTotalBaseAcreage}
                      arcPlcTotalBaseAcreage={arcPlc?.totals?.base_acreage}
                    />
                  </AccordionDetails>
                </Accordion>
              </Box>
            )}

          {[STRESS_TEST_FIELD_NET_INSURANCE_BENEFIT_PER_ACRE, STRESS_TEST_FIELD_INDEMNITY_TOTAL_PER_ACRE].includes(
            stressTestDisplayField
          ) &&
            Boolean(plcStressTestResults?.results?.length) &&
            programElection?.program_election === PROGRAM_NAME_PLC && (
              <Box sx={{ mt: 2 }}>
                <Accordion
                  disableGutters
                  sx={{
                    backgroundColor: "background.paper",
                    "&.Mui-expanded": {
                      backgroundColor: "grey.100",
                    },
                    "&:before": {
                      display: "none",
                    },
                    border: "none",
                    borderRadius: "0.875rem !important",
                    boxShadow: "none",
                    mb: 2,
                  }}
                >
                  <AccordionSummary
                    sx={{
                      padding: 3,
                      flexDirection: "row-reverse",
                      "& .MuiAccordionSummary-content": {
                        margin: "0 0 0 1rem",
                      },
                    }}
                    expandIcon={
                      <ArrowDownIcon
                        sx={{
                          width: 48,
                          height: 48,
                          color: "primary.main",
                          opacity: 0.8,
                          "&:hover": {
                            opacity: 1,
                            color: "primary.dark",
                          },
                        }}
                      />
                    }
                  >
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, width: "100%" }}>
                      <Typography
                        sx={{ fontSize: "1.25rem !important", fontWeight: "600 !important", mb: "0 !important" }}
                        variant="h5"
                      >
                        PLC
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={({ "&>.MuiStack-root": { gap: 1 } }, { maxWidth: "1050px" })}>
                    <PlcStressTestGrid
                      displayFieldName={
                        arcCoDisplayMode === DISPLAY_MODE_BASE_ACRE
                          ? "indemnity_total_per_base_acre"
                          : "indemnity_total_per_acre"
                      }
                      xAxisValue={arcPlc?.totals?.plc_yield}
                      xAxisFieldName={"plc_yield"}
                      xAxisDisplayName={"PLC Yield"}
                      yAxisFieldName={"price"}
                      yAxisDisplayName={"Effective Price"}
                      stressTestResults={plcStressTestResults?.results}
                      doMarkNegativeValues={true}
                      maxLossValue={-1}
                      displayMode={arcCoDisplayMode}
                      budgetTotalReportedAcreage={budgetTotalReportedAcreage || arcPlcTotalBaseAcreage}
                      arcPlcTotalBaseAcreage={arcPlc?.totals?.base_acreage}
                    />
                  </AccordionDetails>
                </Accordion>
              </Box>
            )}

          {arcCoStressTestResults?.results?.length && (
            <>
              <Divider />
              <Typography variant="h1">ARC-CO Comparison</Typography>
              <Box sx={{ maxWidth: "1050px" }}>
                <ArcCoStressTestGrid
                  commodity_name={commodity_name}
                  displayFieldName={
                    arcCoDisplayMode === DISPLAY_MODE_BASE_ACRE
                      ? "indemnity_total_per_base_acre"
                      : "indemnity_total_per_acre"
                  }
                  xAxisFieldName={"county_yield"}
                  xAxisDisplayName={
                    "County Yield" + (arcPlc?.totals?.arccoResponsesByPractice?.length > 1 ? " (Irrigated)" : "")
                  }
                  yAxisFieldName={"price"}
                  yAxisDisplayName={"MYA"}
                  stressTestResults={arcCoStressTestResults?.results}
                  stressTestResultsIrrigated={arcCoStressTestResultsIrrigated?.results}
                  stressTestResultsNonIrrigated={arcCoStressTestResultsNonIrrigated?.results}
                  nonIrrigatedScaleFactor={NonIrrigatedScaleFactor}
                  historicalIrrigationPercentage={historicalIrrigationPercentage}
                  doMarkNegativeValues={true}
                  maxLossValue={-1}
                  displayMode={arcCoDisplayMode}
                  budgetTotalReportedAcreage={budgetTotalReportedAcreage || arcPlcTotalBaseAcreage}
                  arcPlcTotalBaseAcreage={arcPlc?.totals?.base_acreage}
                />
              </Box>
            </>
          )}

          <Box
            className="pageBreakBefore"
            sx={{
              ...(isXXL && {
                width: "100%",
                backgroundColor: (theme) => theme.palette.background.paper,
              }),
            }}
          >
            <Divider />

            <Typography variant="h1" sx={{ paddingLeft: "10px" }}>
              Credit Summary
            </Typography>

            <Grid
              container
              justifyItems="flex-start"
              spacing={1}
              sx={{
                width: "100%",
                padding: "10px 10px 10px 0px",
                backgroundColor: "grey.100",
                margin: "0",
              }}
            >
              <Grid item xs={12} md={12} lg={12} xl={2}>
                <Box component={Stack} direction="column" spacing={0} sx={{ height: "100%", justifyContent: "center" }}>
                  <Stack direction="row" sx={{ alignItems: "center" }}>
                    <CloseOutlined fontSize="large" />
                    <Typography variant="body1">Uninsured</Typography>
                  </Stack>
                </Box>
              </Grid>
              <Grid item xs={12} md={6} lg={2} xl={2}>
                <Stack direction={isSmall ? "row-reverse" : "column"} justifyContent="space-between">
                  <Typography variant="h5">{formatPrice(stressTestResults?.cecl?.loan_amount_simple, 0)}</Typography>
                  <Typography variant="body1" sx={{ textTransform: isSmall ? "" : "uppercase" }}>
                    Loan Amount
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} md={6} lg={2} xl={2}>
                <Stack direction={isSmall ? "row-reverse" : "column"} justifyContent="space-between">
                  <Typography variant="h5">
                    {formatPrice(
                      stressTestResults?.cecl?.loan_amount_simple / budgetCountyPractice?.reported_acreage,
                      0
                    )}
                  </Typography>
                  <Typography variant="body1" sx={{ textTransform: isSmall ? "" : "uppercase" }}>
                    Cost /ac
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} md={6} lg={2} xl={2}>
                <Stack direction={isSmall ? "row-reverse" : "column"} justifyContent="space-between">
                  <Typography variant="h5">
                    {formatPrice(calculateBreakeven(stressTestResults?.cecl?.loan_amount_simple), 2)}
                  </Typography>
                  <Typography variant="body1" sx={{ textTransform: isSmall ? "" : "uppercase" }}>
                    Breakeven
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} md={6} lg={2} xl={2}>
                <Stack direction={isSmall ? "row-reverse" : "column"} justifyContent="space-between">
                  <Typography variant="h5">
                    {formatPrice(
                      stressTestResults?.cecl?.expected_credit_loss_simple / budgetCountyPractice?.reported_acreage,
                      0
                    )}
                  </Typography>
                  <Typography variant="body1" sx={{ textTransform: isSmall ? "" : "uppercase" }}>
                    Expected Loss /ac
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} md={6} lg={4} xl={2}>
                <Stack direction={isSmall ? "row-reverse" : "column"} justifyContent="space-between">
                  <Typography variant="h5">{formatPercentage(stressTestResults?.cecl?.cecl_simple, 2)}</Typography>
                  <Typography variant="body1" sx={{ textTransform: isSmall ? "" : "uppercase" }}>
                    % ECL
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
            <br />

            <Grid container justifyItems="flex-start" spacing={1} sx={{ width: "100%", padding: "10px" }}>
              <Grid item xs={12} md={12} xl={2}>
                <Box component={Stack} direction="column" spacing={0} sx={{ height: "100%", justifyContent: "center" }}>
                  <Stack direction="row" sx={{ alignItems: "center" }}>
                    <InsuranceIcon fontSize="large" />
                    <Typography variant="body1">With Insurance</Typography>
                  </Stack>
                </Box>
              </Grid>
              <Grid item xs={12} md={6} lg={2} xl={2}>
                <Stack direction={isSmall ? "row-reverse" : "column"} justifyContent="space-between">
                  <Typography variant="h5">{formatPrice(stressTestResults?.cecl?.loan_amount_insured, 0)}</Typography>
                  <Typography variant="body1" sx={{ textTransform: isSmall ? "" : "uppercase" }}>
                    Loan Amount
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} md={6} lg={2} xl={2}>
                <Stack direction={isSmall ? "row-reverse" : "column"} justifyContent="space-between">
                  <Typography variant="h5">
                    {formatPrice(
                      stressTestResults?.cecl?.loan_amount_insured / budgetCountyPractice?.reported_acreage,
                      0
                    )}
                  </Typography>
                  <Typography variant="body1" sx={{ textTransform: isSmall ? "" : "uppercase" }}>
                    Cost /ac
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} md={6} lg={2} xl={2}>
                <Stack direction={isSmall ? "row-reverse" : "column"} justifyContent="space-between">
                  <Typography variant="h5">
                    {formatPrice(calculateBreakeven(stressTestResults?.cecl?.loan_amount_insured), 2)}
                  </Typography>
                  <Typography variant="body1" sx={{ textTransform: isSmall ? "" : "uppercase" }}>
                    Breakeven
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} md={6} lg={2} xl={2}>
                <Stack direction={isSmall ? "row-reverse" : "column"} justifyContent="space-between">
                  <Typography variant="h5">
                    {formatPrice(
                      stressTestResults?.cecl?.expected_credit_loss_insured / budgetCountyPractice?.reported_acreage,
                      0
                    )}
                  </Typography>
                  <Typography variant="body1" sx={{ textTransform: isSmall ? "" : "uppercase" }}>
                    Expected Loss /ac
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} md={6} lg={4} xl={2}>
                <Stack direction={isSmall ? "row-reverse" : "column"} justifyContent="space-between">
                  <Typography variant="h5">{formatPercentage(stressTestResults?.cecl?.cecl_insured, 2)}</Typography>
                  <Typography variant="body1" sx={{ textTransform: isSmall ? "" : "uppercase" }}>
                    % ECL
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </>
      )}

      <Divider />

      <Grid container spacing={1}>
        <Grid item xs={12} md={12}>
          {memoizedYieldVarianceChart}
        </Grid>
      </Grid>

      <Dialog open={isOptimizeDialogOpen} fullWidth PaperProps={{ sx: { maxHeight: "100%" } }}>
        <OptimizeInsuranceDialog
          onClose={() => setIsOptimizeDialogOpen(false)}
          insuranceScenario={insuranceScenario}
          budgetCountyPractice={budgetCountyPractice}
          insurancePlans={nonMcoPlans}
          onChange={handleOptimizerOnChange}
        />
      </Dialog>

      <Dialog open={isAddInsuranceDialogOpen} PaperProps={{ sx: { minHeight: "5in" } }}>
        <AddInsuranceDialog
          farmParameters={budgetCountyPractice}
          countyParameters={countyData}
          onSelectPlan={handleAddPlanSelected}
          onClose={() => setIsAddInsuranceDialogOpen(false)}
        />
      </Dialog>

      <Dialog open={isInsuranceDatesDialogOpen} PaperProps={{ sx: { width: "50%" } }}>
        <DialogTitle>Insurance Dates</DialogTitle>
        <DialogContent className="insurance-dates-chart">
          <DatesChart title="" countyPractice={budgetCountyPractice} api={ApiHelper} size="small" />
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="secondary" autoFocus onClick={() => setIsInsuranceDatesDialogOpen(false)}>
            <CloseOutlined sx={{ mr: 1 }} />
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <PremiumDetailsDialog
        open={isPremiumDetailsDialogOpen}
        onClose={() => setIsPremiumDetailsDialogOpen(false)}
        calculatePremiumsResponse={calculatePremiumsResponse}
        selectedInsurancePlanName={selectedInsurancePlanName}
      />

      <WorkspaceActionBar
        content={
          <>
            {!mcoOnlyScenario && (
              <Button variant="contained" onClick={() => setIsOptimizeDialogOpen(true)}>
                <AddCircleOutline sx={{ mr: 1 }} />
                Optimize {!isSmall && " Insurance"}
              </Button>
            )}
            <Button variant="contained" onClick={() => setIsAddInsuranceDialogOpen(true)}>
              <AddCircleOutline sx={{ mr: 1 }} />
              {!isSmall && "Add "} Plan
            </Button>
          </>
        }
      />

    </>
  );
}
